/**
 * Telegram Environment Detection and Management
 * 
 * Detects whether the app is running inside Telegram or in a regular browser,
 * and provides utilities for mocking the Telegram SDK in browser mode.
 */

import { mockTelegramEnv, parseInitData } from '@telegram-apps/sdk-react';

export type AuthMode = 'telegram' | 'browser';

export interface BrowserAuthUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: string;
  hash: string;
}

const BROWSER_AUTH_STORAGE_KEY = 'browser_auth_user';

/**
 * Detects if the app is running inside Telegram WebApp
 */
export const isTelegramWebApp = (): boolean => {
  if (typeof window === "undefined") return false;

  const tg = (
    window as Window & {
      Telegram?: {
        WebApp?: {
          initData?: string;
          initDataUnsafe?: { user?: { id?: number } };
          platform?: string;
        };
      };
    }
  ).Telegram;
  const webApp = tg?.WebApp;
  if (webApp?.initData) return true;
  if (webApp?.initDataUnsafe?.user?.id) return true;

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has("tgWebAppData") || urlParams.has("tgWebAppStartParam")) {
    return true;
  }

  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  if (hashParams.has("tgWebAppData")) return true;

  return false;
};

/**
 * Gets the current auth mode based on environment
 */
export const getAuthMode = (): AuthMode => {
  // Always use telegram mode if we detect we're in Telegram
  if (isTelegramWebApp() || import.meta.env.DEV) {
    return 'telegram';
  }
  return 'browser';
};

/**
 * Stores browser auth user data in localStorage
 */
export const storeBrowserAuthUser = (user: BrowserAuthUser): void => {
  localStorage.setItem(BROWSER_AUTH_STORAGE_KEY, JSON.stringify(user));
};

/**
 * Retrieves browser auth user data from localStorage
 */
export const getBrowserAuthUser = (): BrowserAuthUser | null => {
  const stored = localStorage.getItem(BROWSER_AUTH_STORAGE_KEY);
  if (!stored) return null;
  
  try {
    return JSON.parse(stored) as BrowserAuthUser;
  } catch {
    return null;
  }
};

/**
 * Clears browser auth user data from localStorage
 */
export const clearBrowserAuthUser = (): void => {
  localStorage.removeItem(BROWSER_AUTH_STORAGE_KEY);
};

/**
 * Creates mock initData string from browser auth user
 */
const createMockInitDataRaw = (user: BrowserAuthUser): string => {
  // Placeholder signature for SDK compatibility (not used for actual validation)
  const placeholderSignature = '0000000000000000000000000000000000000000000000000000000000000000';
  
  const initDataParams = new URLSearchParams([
    ['user', JSON.stringify({
      id: user.id,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      username: user.username || '',
      language_code: 'en',
      is_premium: false,
      allows_write_to_pm: true,
    })],
    ['hash', user.hash],
    ['signature', placeholderSignature],
    ['auth_date', user.auth_date],
    ['chat_type', 'sender'],
    ['chat_instance', '8428209589180549439'],
  ]);
  return initDataParams.toString();
};

/**
 * Mocks the Telegram SDK environment for browser-authenticated users
 */
export const mockTelegramEnvForBrowserUser = (user: BrowserAuthUser): void => {
  const initDataRaw = createMockInitDataRaw(user);

  mockTelegramEnv({
    themeParams: {
      accentTextColor: '#6ab2f2',
      bgColor: '#17212b',
      buttonColor: '#5288c1',
      buttonTextColor: '#ffffff',
      destructiveTextColor: '#ec3942',
      headerBgColor: '#17212b',
      hintColor: '#708499',
      linkColor: '#6ab3f3',
      secondaryBgColor: '#232e3c',
      sectionBgColor: '#17212b',
      sectionHeaderTextColor: '#6ab3f3',
      subtitleTextColor: '#708499',
      textColor: '#f5f5f5',
    },
    initData: parseInitData(initDataRaw),
    initDataRaw,
    version: '7.2',
    platform: 'tdesktop',
  });

  console.log('[TelegramEnvironment] Mocked Telegram SDK for browser user:', user.id);
};

/**
 * Checks if user is authenticated in browser mode
 */
export const isBrowserAuthenticated = (): boolean => {
  return getBrowserAuthUser() !== null;
};

